<div class="relative flex h-full min-h-0 w-full flex-col !items-stretch overflow-hidden bg-slate-50 text-gray-900">
  <div class="flex w-full shrink-0 items-center gap-3 border-b border-gray-100 bg-white px-4 py-3">
    @if (contact) {
      <app-avatar [title]="displayName" size="40px" [isPhotoGallery]="false" />
    }
    <div class="min-w-0 flex-1">
      <div class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ 'contact' | translate }}</div>
      @if (contact) {
        <div class="truncate text-lg font-bold leading-6 text-gray-900">{{ displayName || '-' }}</div>
      } @else {
        <div class="mt-1 h-6 w-48 animate-pulse rounded bg-gray-200"></div>
      }
    </div>
    @if (contact) {
      <button
        type="button"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-rose-50 hover:text-rose-600"
        [matTooltip]="'delete' | translate"
        [disabled]="busy"
        (click)="remove()"
      >
        <app-icon>delete</app-icon>
      </button>
    }
    <button
      type="button"
      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
      [attr.aria-label]="'close' | translate"
      (click)="requestClose()"
    >
      <app-icon>close</app-icon>
    </button>
  </div>

  <div class="crm-scroll min-h-0 w-full flex-1 overflow-y-auto overflow-x-hidden p-4">
    @if (loading) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div class="h-80 animate-pulse rounded-lg bg-white"></div>
        <div class="h-80 animate-pulse rounded-lg bg-white"></div>
      </div>
    } @else if (contact) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(320px,0.42fr)_minmax(420px,0.58fr)]">
        <div class="flex min-w-0 flex-col gap-4">
          <div class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="mb-3 border-b border-slate-100 pb-3 text-base font-semibold text-slate-950">
              {{ 'board_v3_contact_info' | translate }}
            </div>
            <div class="grid gap-3">
              @for (field of nameFields; track field.key) {
                <div class="grid gap-1">
                  <div class="text-xs font-medium text-slate-500">{{ field.label | translate }}</div>
                  @if (isFieldEditing(field.key)) {
                    <input
                      #editableControl
                      class="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400"
                      [class.border-red-400]="hasFieldError(field.key)"
                      [formControl]="form.controls[field.key]"
                      maxlength="255"
                      (focusout)="onTextFieldBlur($event)"
                    />
                  } @else {
                    <button
                      type="button"
                      class="flex h-10 w-full cursor-text items-center overflow-x-auto whitespace-nowrap !rounded-lg bg-slate-50 px-3 text-left text-sm font-medium text-slate-800"
                      data-contact-edit-trigger="true"
                      (click)="editField(field.key)"
                    >
                      {{ form.controls[field.key].value || '-' }}
                    </button>
                  }
                  @if (hasFieldError(field.key)) {
                    <p class="m-0 text-xs text-red-500">{{ 'input_errors.required' | translate }}</p>
                  }
                </div>
              }

              @for (field of ['phones', 'emails']; track field) {
                <div class="grid gap-1">
                  <div class="text-xs font-medium text-slate-500">{{ field | translate }}</div>
                  @if (isFieldEditing($any(field))) {
                    <div #editableControl class="flex flex-col gap-2" data-contact-edit-trigger="true">
                      @for (control of getFieldArray($any(field)).controls; track control) {
                        <div class="flex items-center gap-2">
                          <input
                            [type]="field === 'phones' ? 'tel' : 'email'"
                            class="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400"
                            [class.border-red-400]="control.invalid && control.touched"
                            [formControl]="control"
                            (input)="field === 'phones' ? onPhoneInput(control, $event) : null"
                          />
                          <button
                            type="button"
                            class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                            (click)="removeRow($any(field), $index)"
                          >
                            <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">delete</app-icon>
                          </button>
                        </div>
                        @if (control.invalid && control.touched) {
                          <p class="m-0 text-xs text-red-500">
                            {{ (field === 'phones' ? 'input_errors.phone' : 'input_errors.email') | translate }}
                          </p>
                        }
                      }
                      <button
                        type="button"
                        class="flex w-fit items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700"
                        (click)="addRow($any(field))"
                      >
                        <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
                        {{ (field === 'phones' ? 'add_phone' : 'add_email') | translate }}
                      </button>
                    </div>
                  } @else {
                    <div class="flex flex-col gap-1.5">
                      @for (control of getFieldArray($any(field)).controls; track control) {
                        @if (control.value.trim()) {
                          <div
                            class="relative min-h-9 cursor-text break-all rounded-lg bg-slate-50 px-3 py-2 pr-10 text-left text-sm font-medium text-slate-800"
                            data-contact-edit-trigger="true"
                            (click)="editField($any(field))"
                          >
                            {{ field === 'phones' ? formatContactPhone(control.value) : control.value }}
                            <a
                              class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                              [href]="field === 'phones' ? phoneHref(control.value) : 'mailto:' + control.value"
                              [title]="(field === 'phones' ? 'call' : 'email') | translate"
                              (click)="$event.stopPropagation()"
                              ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">{{
                                field === 'phones' ? 'phone' : 'mail_outline'
                              }}</app-icon></a
                            >
                          </div>
                        }
                      }
                      @if (!hasRowValues($any(field))) {
                        <button
                          type="button"
                          class="min-h-9 w-full cursor-text !rounded-lg bg-slate-50 px-3 py-2 text-left text-sm font-medium text-slate-800"
                          data-contact-edit-trigger="true"
                          (click)="editField($any(field))"
                        >
                          -
                        </button>
                      }
                    </div>
                  }
                </div>
              }

              <div class="grid gap-1">
                <div class="text-xs font-medium text-slate-500">{{ 'addresses' | translate }}</div>
                @if (isFieldEditing('addresses')) {
                  <div #editableControl data-contact-edit-trigger="true">
                    <app-board-v3-address-editor [formControl]="addressesControl" />
                  </div>
                } @else {
                  <div class="flex flex-col gap-1.5">
                    @for (address of addressesControl.value; track $index) {
                      <div
                        class="relative min-h-9 cursor-text break-words rounded-lg bg-slate-50 px-3 py-2 pr-10 text-left text-sm font-medium text-slate-800"
                        data-contact-edit-trigger="true"
                        (click)="editField('addresses')"
                      >
                        {{ formatAddress(address) }}
                        <a
                          class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                          [href]="getAddressMapUrl(address)"
                          target="_blank"
                          rel="noopener noreferrer"
                          [title]="'open_in_maps' | translate"
                          (click)="$event.stopPropagation()"
                          ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">location_on</app-icon></a
                        >
                      </div>
                    } @empty {
                      <button
                        type="button"
                        class="min-h-9 w-full cursor-text !rounded-lg bg-slate-50 px-3 py-2 text-left text-sm font-medium text-slate-800"
                        data-contact-edit-trigger="true"
                        (click)="editField('addresses')"
                      >
                        -
                      </button>
                    }
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div
              class="mb-3 flex items-center gap-2 border-b border-slate-100 pb-3 text-base font-semibold text-slate-950"
            >
              {{ 'board_v3_contact_linked_cards' | translate }}
              <span class="rounded-full bg-slate-100 px-2 text-xs font-semibold text-slate-500">{{
                contact.cards.length
              }}</span>
            </div>
            <div class="flex flex-col gap-1.5">
              @for (card of contact.cards; track card.id) {
                <div class="group flex min-w-0 items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                  <button
                    type="button"
                    class="flex min-w-0 flex-1 flex-col text-left disabled:cursor-default"
                    [disabled]="!canOpenCards"
                    (click)="openCard(card)"
                  >
                    <span
                      class="truncate text-sm font-semibold text-slate-900"
                      [ngClass]="{ 'group-hover:text-blue-600': canOpenCards }"
                      >{{ card.name }}</span
                    >
                    <span class="truncate text-xs text-slate-500">
                      {{ getCardTypeKey(card) | translate }}
                      @if (getFunnelName(card)) {
                        · {{ getFunnelName(card) }}
                      }
                    </span>
                  </button>
                  <button
                    type="button"
                    class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    [matTooltip]="'board_v3_contact_unlink' | translate"
                    [disabled]="busy"
                    (click)="unlinkCard(card)"
                  >
                    <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">link_off</app-icon>
                  </button>
                </div>
              } @empty {
                <div class="flex min-h-16 items-center justify-center rounded-lg bg-slate-50 text-sm text-slate-400">
                  {{ 'board_v3_contact_no_cards' | translate }}
                </div>
              }
            </div>
          </div>
        </div>

        <div class="flex min-w-0 flex-col gap-4">
          <app-timeline
            class="block [&_h2]:!m-0 [&_h2]:!text-base [&_h2]:!font-semibold [&_h2]:!leading-6"
            [entityType]="TIMELINE_ENTITY_TYPE.CONTACT"
            [entityId]="contact.id"
            [filters]="TIMELINE_FILTERS"
            [fieldDefinitions]="fieldDefinitions"
            [canPrefillReminders]="false"
            (contactChanged)="reload()"
          />
        </div>
      </div>
    }
  </div>

  <div
    class="w-full shrink-0 border-t border-slate-200 bg-white px-5 py-3 shadow-[0_-4px_12px_rgba(15,23,42,0.04)]"
    [class.hidden]="!contact"
  >
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex min-h-10 items-center gap-2 text-sm">
        @if (hasChanges) {
          <span class="h-2 w-2 shrink-0 rounded-full bg-amber-400"></span>
          <span class="font-medium text-amber-700">{{ 'unsaved_changes' | translate }}</span>
        } @else {
          <span class="h-2 w-2 shrink-0 rounded-full bg-emerald-500"></span>
          <span class="font-medium text-slate-500">{{ 'changes_saved' | translate }}</span>
        }
      </div>

      <div class="flex shrink-0 gap-2">
        <app-button
          btnType="stroked"
          styleType="rounded"
          [customClass]="'!h-10 min-w-[132px] !rounded-lg !px-5 text-sm font-semibold !leading-none [&.btn-stroked.btn-stroked]:!border-slate-950 [&.btn-stroked.btn-stroked]:!text-slate-950 hover:[&.btn-stroked.btn-stroked]:!bg-slate-50 active:[&.btn-stroked.btn-stroked]:!bg-slate-100 [--mat-button-outlined-hover-state-layer-opacity:0] [--mat-button-outlined-focus-state-layer-opacity:0] [--mat-button-outlined-pressed-state-layer-opacity:0] [&.btn-stroked.btn-stroked]:disabled:!border-slate-200 [&.btn-stroked.btn-stroked]:disabled:!bg-white [&.btn-stroked.btn-stroked]:disabled:!text-slate-300'"
          [disabled]="saving || !hasChanges"
          (onChangeClick)="cancelChanges()"
        >
          {{ 'cancel' | translate }}
        </app-button>
        <app-button
          styleType="rounded"
          [customClass]="'!h-10 min-w-[132px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0] [&.btn-default.btn-default]:disabled:!bg-slate-200 [&.btn-default.btn-default]:disabled:!text-slate-400'"
          [disabled]="saving || !hasChanges"
          (onChangeClick)="save()"
        >
          {{ (saving ? 'saving' : 'save') | translate }}
        </app-button>
      </div>
    </div>
  </div>

  @if (showCloseConfirmation) {
    <div class="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/35 p-5">
      <div class="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-2xl">
        <div class="flex items-start gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">edit</app-icon>
          </div>
          <div class="min-w-0">
            <div class="text-base font-semibold text-slate-950">{{ 'unsaved_changes' | translate }}</div>
            <p class="mb-0 mt-1 text-sm leading-5 text-slate-600">
              {{ 'board_v3_contact_save_before_closing' | translate }}
            </p>
          </div>
        </div>

        <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <app-button
            btnType="stroked"
            styleType="rounded"
            [customClass]="'!h-11 min-w-[132px] !rounded-lg !px-5 text-sm font-semibold !leading-none'"
            [disabled]="saving"
            (onChangeClick)="cancelClose()"
          >
            {{ 'cancel' | translate }}
          </app-button>
          <app-button
            btnType="cancel"
            styleType="rounded"
            [customClass]="'!h-11 min-w-[148px] !rounded-lg !bg-rose-600 !px-5 text-sm font-semibold !leading-none hover:!bg-rose-700 active:!bg-rose-800'"
            [disabled]="saving"
            (onChangeClick)="discardChangesAndClose()"
          >
            {{ 'dont_save' | translate }}
          </app-button>
          <app-button
            styleType="rounded"
            [customClass]="'!h-11 min-w-[132px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800'"
            [disabled]="saving"
            (onChangeClick)="save(true)"
          >
            {{ (saving ? 'saving' : 'save') | translate }}
          </app-button>
        </div>
      </div>
    </div>
  }
</div>
